import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DomainTab } from './helpers/validate-gating-settings';

import CustomSelect from '@ui/select/custom-select/custom-select';
import { checkIsDomainInvalid } from '@/utils/domain-regex';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import Text from '@ui/text/text/text';
import normalizeDomain from './helpers/normalize-domain';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import SwitchV2 from '@ui/switch-v2/switch-v2';
import SettingsEditPanel from '../../../../controls/settings-edit-panel';
import SettingsToggle from '../../../../controls/settings-toggle';
import validateGatingSettings from './helpers/validate-gating-settings';
import { GateTypes } from '@/types/registration.types';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectWorkingProjectRegistrationQuestions,
  selectWorkingProjectRegistrationSettings,
  selectWorkingProjectRegistrationSteps,
  selectWorkingProjectRequiredRegistrationQuestions,
} from '@/store/slices/project/selectors';
import { getRegistrationQuestions } from '@/store/slices/channel/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import { deriveRegistrationUpdate } from '../../helpers/derive-registration-update';
import Container from '@ui/container/container';

type Props = {
  isSelected: boolean;
  onToggle: (on: boolean) => void;
  disabled?: boolean;
};

const DomainGateTypeOption = ({ isSelected, onToggle, disabled = false }: Props) => {
  const dispatch = useAppDispatch();
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const registrationSteps = useAppSelector(selectWorkingProjectRegistrationSteps);
  const registrationQuestions = useAppSelector(selectWorkingProjectRegistrationQuestions);
  const requiredRegistrationQuestions = useAppSelector(selectWorkingProjectRequiredRegistrationQuestions);
  const channelRegistrationQuestions = useAppSelector(getRegistrationQuestions);

  const requirementsMap = registrationSettings?.requirements;
  const sourceBlockedDomains = registrationSettings?.blockedDomainEmails;
  const sourceAllowDomains = requirementsMap?.[GateTypes.domain_email]?.allowedDomains;
  const sourceDomainTab: DomainTab =
    registrationSettings?.domain_gate_mode ??
    (sourceAllowDomains?.length ? 'allow' : sourceBlockedDomains?.length ? 'block' : 'allow');

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [workingAllowDomains, setWorkingAllowDomains] = useState<string[]>(sourceAllowDomains || []);
  const [workingBlockDomains, setWorkingBlockDomains] = useState<string[]>(sourceBlockedDomains || []);
  const [workingDomainTab, setWorkingDomainTab] = useState<DomainTab>(sourceDomainTab);

  useEffect(() => {
    setWorkingAllowDomains(sourceAllowDomains || []);
    setWorkingBlockDomains(sourceBlockedDomains || []);
    setWorkingDomainTab(sourceDomainTab);
  }, [sourceAllowDomains, sourceBlockedDomains, sourceDomainTab]);

  useEffect(() => {
    if (!isSelected) {
      setDirty(false);
      setError('');
    }
  }, [isSelected]);

  const gateError = useMemo(() => {
    if (!isSelected) return '';
    const errors = validateGatingSettings({
      access: GateTypes.domain_email,
      domainTab: workingDomainTab,
      allowDomains: workingAllowDomains,
      blockDomains: workingBlockDomains,
      sharedPasscode: '',
    });
    return errors?.domain_email || '';
  }, [isSelected, workingDomainTab, workingAllowDomains, workingBlockDomains]);

  const handleSave = useCallback(() => {
    if (!registrationSettings) return;
    const update = deriveRegistrationUpdate(
      {
        registration_settings: registrationSettings,
        registration_steps: registrationSteps,
        registration_questions: registrationQuestions,
        required_registration_questions: requiredRegistrationQuestions,
      },
      {
        kind: 'gate',
        gateType: GateTypes.domain_email,
        domainGateMode: workingDomainTab,
        allowDomains: workingAllowDomains,
        blockDomains: workingBlockDomains,
        sharedPasscode: requirementsMap?.[GateTypes.shared_passcode]?.sharedPasscode || '',
      },
      channelRegistrationQuestions
    );
    dispatch(saveWorkingProjectByFields({ saveId: 'gating-settings', ...update }));
  }, [
    channelRegistrationQuestions,
    dispatch,
    registrationQuestions,
    registrationSettings,
    registrationSteps,
    requiredRegistrationQuestions,
    requirementsMap,
    workingAllowDomains,
    workingBlockDomains,
    workingDomainTab,
  ]);

  const handleCancel = useCallback(() => {
    setWorkingAllowDomains(sourceAllowDomains || []);
    setWorkingBlockDomains(sourceBlockedDomains || []);
    setWorkingDomainTab(sourceDomainTab);
  }, [sourceAllowDomains, sourceBlockedDomains, sourceDomainTab]);

  const handleEnterKeyPress = useCallback(
    (inputValue: string) => {
      const normalized = normalizeDomain(inputValue);
      const isInvalidDomain = checkIsDomainInvalid(normalized);
      if (isInvalidDomain) {
        setError(isInvalidDomain);
        return;
      }
      if (inputRef?.current) {
        inputRef.current.value = '';
      }
      const setter = workingDomainTab === 'allow' ? setWorkingAllowDomains : setWorkingBlockDomains;
      setter((prev) => (prev.includes(normalized) ? prev : Array.from(new Set([...prev, normalized]))));
    },
    [workingDomainTab]
  );

  const userValues: TReactSelectOption[] = useMemo(() => {
    const source = workingDomainTab === 'allow' ? workingAllowDomains : workingBlockDomains;
    return source.map((domain) => ({ label: domain, value: domain }));
  }, [workingDomainTab, workingAllowDomains, workingBlockDomains]);

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      if (!error) return;
      const value = (event.target as HTMLInputElement)?.value || '';
      if (!value || !checkIsDomainInvalid(value)) {
        setError('');
      }
    },
    [error]
  );

  const handleBlur = useCallback(() => {
    setError('');
    setDirty(true);
  }, []);

  const handleSelectChange = useCallback(
    (selected: TReactSelectOption[]) => {
      const next = selected.map((option) => normalizeDomain(option.value.toString()));
      if (workingDomainTab === 'allow') {
        setWorkingAllowDomains(next);
      } else {
        setWorkingBlockDomains(next);
      }
    },
    [workingDomainTab]
  );

  const shouldShowError = useMemo(() => {
    const _error = error || gateError || '';
    if (_error.includes('Must include at least one')) {
      return dirty ? _error : '';
    }
    return _error;
  }, [error, gateError, dirty]);

  return (
    <SettingsEditPanel
      isEditing={isSelected}
      onSave={handleSave}
      onCancel={handleCancel}
      cancelLabel="Reset"
      saveDisabled={!!gateError}
      warningMessage={shouldShowError}
      expandContent={
        <Container className="padding-h-16">
          <Container className="flex-column gap-16">
            <SwitchV2
              value={workingDomainTab}
              on={workingDomainTab === 'block'}
              height={24}
              offLabel={
                <Text whiteSpace="nowrap" design="GlobalIcoText1" className="C1 flex ai-center gap-6">
                  <Icon skeletonLoader skeletonLoaderRadius="100%" name={ICONS.ROUND_CHECK} size={12} />{' '}
                  <span>Allow</span>
                </Text>
              }
              onLabel={
                <Text whiteSpace="nowrap" design="GlobalIcoText1" className="C1 flex ai-center gap-6">
                  <Icon
                    skeletonLoader
                    skeletonLoaderRadius="100%"
                    name={ICONS.BAN_FILLED}
                    size={12}
                    color="var(--global-ico-text-3)"
                  />{' '}
                  <span>Block</span>
                </Text>
              }
              onClick={(_, nextOn) => setWorkingDomainTab(nextOn ? 'block' : 'allow')}
            />
            <CustomSelect
              hideMenu
              hideHeader
              direction="vertical"
              inputRef={inputRef}
              selectFirstSearchOptionOnEnter
              hideDropdownIcon
              id="domain-select"
              options={[]}
              Placeholder="Press ENTER after each domain input"
              controlledOptions={userValues}
              onEnterKeyPress={handleEnterKeyPress}
              onKeyUp={handleKeyUp}
              onInputBlur={handleBlur}
              onChange={handleSelectChange}
              maxRows={2}
            />
          </Container>
        </Container>
      }
    >
      <SettingsToggle
        label="Domains"
        value={isSelected}
        sKey={GateTypes.domain_email.toString()}
        onToggle={(_, on) => onToggle(on)}
        disabled={disabled}
        tooltipText="Choose a list of domains that are able to join OR a list of domains that are blocked"
      />
    </SettingsEditPanel>
  );
};

export default memo(DomainGateTypeOption);
